import {
  Menu,
  MenuButton,
  MenuDivider,
  MenuList,
  MenuPopover,
  MenuTrigger,
  mergeClasses,
} from '@fluentui/react-components';
import { Editor } from '@tiptap/react';

import ToolbarMenuItem from './ToolbarMenuItem';
import { TSparseOrganization } from '@modules/users';
import { ETiptapSelectorKey } from '../../../types/tiptap.types';
import useEditorActive from '../../../hooks/useEditorActive.hook';
import { ECustomStyleScope } from '@modules/users/types/organizations.types';
import { TEMPLATE_FIELD_BLOCK_NAME, TEMPLATE_FIELD_INLINE_NAME } from '../../../doc-engine/templateFields';
import { isLockedOutsideFields } from '../../../tiptap/extensions/TemplateLocking';
import useToolbarButtonStyles from '../../../hooks/useToolbarButtonStyles.hook';

type TProps = {
  editor: Editor;
  org: TSparseOrganization;
  classes: Record<'menu' | 'menuButton', string>;
};

const TextStyleDropdown = ({ editor, org, classes }: TProps) => {
  const availableHeadings = !org.mock.isH5RefEnabled ? 5 : 4;
  const inlineStyles = org.stylesConfig.customStyles.filter((style) => style.scope === ECustomStyleScope.INLINE);
  const blockStyles = org.stylesConfig.customStyles.filter((style) => style.scope === ECustomStyleScope.BLOCK);
  const hasCustomStyles = inlineStyles.length > 0 || blockStyles.length > 0;

  const headingLevel = useEditorActive((editor) => editor.getAttributes(ETiptapSelectorKey.HEADING).level);
  const activeInlineStyleKey = useEditorActive(
    (editor) => editor.getAttributes(ETiptapSelectorKey.CUSTOM_STYLE_MARK).customStyleKey
  );
  const activeBlockStyleKey = useEditorActive((editor) => editor.getAttributes('paragraph').customStyleKey);
  // Template fields exclude headings by schema; custom styles and Normal text stay available.
  const inTemplateField = useEditorActive(
    (editor) => editor.isActive(TEMPLATE_FIELD_BLOCK_NAME) || editor.isActive(TEMPLATE_FIELD_INLINE_NAME)
  );
  // The locked body of a bound document silently filters every style change.
  const locked = useEditorActive((editor) => isLockedOutsideFields(editor.state));

  const activeInlineStyle = inlineStyles.find((style) => style.id === activeInlineStyleKey);
  const activeBlockStyle = blockStyles.find((style) => style.id === activeBlockStyleKey);

  const buttonLabel =
    activeBlockStyle?.label || activeInlineStyle?.label || (headingLevel ? `Heading ${headingLevel}` : 'Normal text');

  const handleNormalTextClick = () => {
    // If on a heading, convert to paragraph. Otherwise, just clear the styles.
    // Calling setParagraph() on a paragraph inside a section triggers clearNodes() which
    // attempts to lift content, causing "Invalid content for node type section" error.
    if (headingLevel) {
      editor.chain().focus().setInlineStyle(null).setParagraphStyle(null).setParagraph().run();
    } else {
      editor.chain().focus().setInlineStyle(null).setParagraphStyle(null).run();
    }
  };

  const handleHeadingClick = (level: number) => {
    editor
      .chain()
      .focus()
      .setInlineStyle(null)
      .toggleHeading({ level: level as 1 | 2 | 3 | 4 | 5 })
      .run();
  };

  const handleInlineStyleClick = (styleId: string) => {
    editor.chain().focus().setInlineStyle(styleId).run();
  };

  const handleBlockStyleClick = (styleId: string) => {
    // If on a heading, convert to paragraph first. Otherwise, just set the paragraph style.
    // Calling setParagraph() on a paragraph inside a section triggers clearNodes() which
    // attempts to lift content, causing "Invalid content for node type section" error.
    if (headingLevel) {
      editor.chain().focus().setInlineStyle(null).setParagraph().setParagraphStyle(styleId).run();
    } else {
      editor.chain().focus().setInlineStyle(null).setParagraphStyle(styleId).run();
    }
  };

  const toolbarClasses = useToolbarButtonStyles();

  return (
    <Menu>
      <MenuTrigger disableButtonEnhancement>
        <MenuButton className={mergeClasses(classes.menuButton, locked && toolbarClasses.disabled)} disabled={locked}>
          {buttonLabel}
        </MenuButton>
      </MenuTrigger>

      <MenuPopover className={classes.menu}>
        <MenuList>
          <ToolbarMenuItem
            isActive={headingLevel === undefined && !activeInlineStyle && !activeBlockStyle}
            onClick={handleNormalTextClick}
          >
            Normal text
          </ToolbarMenuItem>

          {Array.from({ length: availableHeadings }).map((_, idx) => (
            <ToolbarMenuItem
              key={`heading-${idx}`}
              isActive={headingLevel === idx + 1}
              disabled={inTemplateField}
              onClick={() => handleHeadingClick(idx + 1)}
            >
              Heading {idx + 1}
            </ToolbarMenuItem>
          ))}

          {hasCustomStyles && <MenuDivider />}

          {inlineStyles.map((style) => (
            <ToolbarMenuItem
              key={style.id}
              isActive={activeInlineStyleKey === style.id}
              // Inline styles are marks; headings allow no marks. (Block styles stay enabled:
              // they convert the heading to a styled paragraph, like Normal text.)
              disabled={headingLevel !== undefined}
              onClick={() => handleInlineStyleClick(style.id)}
            >
              {style.label} (inline)
            </ToolbarMenuItem>
          ))}

          {blockStyles.map((style) => (
            <ToolbarMenuItem
              key={style.id}
              isActive={activeBlockStyleKey === style.id}
              onClick={() => handleBlockStyleClick(style.id)}
            >
              {style.label} (block)
            </ToolbarMenuItem>
          ))}
        </MenuList>
      </MenuPopover>
    </Menu>
  );
};

export default TextStyleDropdown;
